import { ChevronRight } from "lucide-react";

import { UserAvatar } from "@/components/common/UserAvatar";
import { cn } from "@/lib/utils";
import { identityRingClass, identityWashClass } from "@/lib/card-styles";
import { typeSubtitleClass, typeCaptionClass } from "@/lib/typography";

export interface ProfileIdentityRowProps {
  userId: string | null;
  displayName?: string | null;
  email: string;
  avatarUrl?: string | null;
  onClick: () => void;
  /** Screen-reader label; falls back to the learner's own name. */
  ariaLabel?: string;
}

/**
 * Who you are, at the top of the profile menu, and the way into the profile
 * modal.
 *
 * ## Why it is a row and not a label
 *
 * This used to be a `DropdownMenuLabel` with an `onClick` that opened the
 * modal — visually a heading, behaviourally a button, with nothing on screen
 * saying so. It is now a real menu row: same hit area, same hover, same
 * keyboard handling as every other row, with a chevron as the affordance that
 * it leads somewhere rather than being a boundary.
 *
 * ## The gradient fallback is intentional, and only here
 *
 * The design system bans gradient *hero CTAs* because the amber end pulled
 * focus off a serif title. This is a 48px avatar whose fallback only appears
 * before someone uploads a photo, where there is no title to compete with —
 * and the wash behind it is the same stone→paper→amber `PageHero` uses for a
 * background, the opposite end of the ramp from `primaryCtaClass`.
 */
export function ProfileIdentityRow({
  userId,
  displayName,
  email,
  avatarUrl,
  onClick,
  ariaLabel,
}: ProfileIdentityRowProps) {
  const name = displayName?.trim() || email;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel ?? name}
      className={cn(
        "group/identity relative flex w-full cursor-pointer items-center gap-3 overflow-hidden rounded-xl px-2.5 py-2.5 text-left outline-none",
        "transition-colors focus-visible:ring-2 focus-visible:ring-ring",
        identityWashClass,
      )}
    >
      <UserAvatar
        userId={userId}
        displayName={displayName ?? undefined}
        email={email}
        avatarUrl={avatarUrl ?? undefined}
        size="xl"
        className={cn("h-12 w-12 shrink-0 shadow-sm", identityRingClass)}
        fallbackClassName="bg-primary-strong text-white font-semibold"
      />
      <span className="min-w-0 flex-1">
        <span className={cn("block truncate text-[15px] font-bold", typeSubtitleClass)}>{name}</span>
        {displayName?.trim() ? (
          <span className={cn("mt-0.5 block truncate text-xs", typeCaptionClass)}>{email}</span>
        ) : null}
      </span>
      <ChevronRight
        className="h-4 w-4 shrink-0 text-stone-400 transition-transform duration-200 group-hover/identity:translate-x-0.5 dark:text-stone-500"
        aria-hidden="true"
      />
    </button>
  );
}

export default ProfileIdentityRow;